<template>
  <div class="publish-product-page">
    <NavBar class="publish-product-page__nav" :title="$t('publishProduct.title')" fixed placeholder left-arrow
      @click-left="$router.back()" />

    <div class="publish-product-page__content">
      <div class="publish-card">
        <div class="publish-card__title">{{ $t('publishProduct.formTitle') }}</div>

        <div class="form-field">
          <div class="form-field__label">{{ $t('publishProduct.name') }}</div>
          <Field v-model.trim="form.name" class="publish-input"
            :placeholder="$t('publishProduct.namePlaceholder')" />
        </div>

        <div class="form-field">
          <div class="form-field__label">{{ $t('publishProduct.price') }}</div>
          <Field v-model.trim="form.price" class="publish-input" type="number"
            :placeholder="$t('publishProduct.pricePlaceholder')" />
        </div>

        <div class="form-field product-image-field">
          <div class="form-field__label">{{ $t('publishProduct.image') }}</div>
          <div class="uploader-row">
            <Uploader v-model="fileList" accept="image/*" :after-read="uploadImage" :max-count="1"
              :show-upload="fileList.length === 0" @delete="deleteImage">
              <div class="upload-button">
                <Icon name="plus" />
                <div>{{ $t('publishProduct.upload') }}</div>
              </div>
            </Uploader>
            <div class="file-name">{{ selectedFileName }}</div>
          </div>
        </div>

        <div class="form-field">
          <div class="form-field__label">{{ $t('publishProduct.description') }}</div>
          <Field v-model.trim="form.description" class="publish-input publish-input--textarea" type="textarea" rows="4"
            :placeholder="$t('publishProduct.descriptionPlaceholder')" />
        </div>

        <Button class="submit-button" native-type="button" :disabled="uploading || submitting" :loading="submitting"
          @click="submitProduct">
          {{ $t('publishProduct.confirm') }}
        </Button>
      </div>

      <div class="products-card">
        <div class="products-card__title">{{ $t('publishProduct.myProducts') }}</div>
        <Products ref="myProducts" :user-id="userId" user-only />
      </div>
    </div>
  </div>
</template>

<script>
import { Button, Field, Icon, NavBar, Toast, Uploader } from 'vant'
import { mapGetters } from 'vuex'
import { PRODUCT_ADD } from '@/api'
import { $upload } from '@/utils/request.js'
import Products from './component/popularProducts.vue'

export default {
  name: 'PublishProduct',
  components: {
    Button,
    Field,
    Icon,
    NavBar,
    Products,
    Uploader,
  },
  data() {
    return {
      form: {
        name: '',
        price: '',
        image: '',
        description: '',
      },
      fileList: [],
      submitting: false,
    }
  },
  computed: {
    ...mapGetters(['userInfo']),
    userId() {
      return this.userInfo.id || ''
    },
    uploading() {
      return this.fileList.some(file => file.status === 'uploading')
    },
    selectedFileName() {
      const item = this.fileList[0]
      return (item && item.file && item.file.name) || this.$t('publishProduct.noFile')
    },
  },
  created() {
    this.$store.dispatch('getUserInfo')
  },
  methods: {
    readImage(file) {
      return new Promise((resolve, reject) => {
        const image = new Image()
        const reader = new FileReader()

        reader.onload = event => {
          image.src = event.target.result
        }
        reader.onerror = reject
        image.onload = () => resolve(image)
        image.onerror = reject
        reader.readAsDataURL(file)
      })
    },
    async compressImage(file) {
      const image = await this.readImage(file)
      const maxSize = 1000
      let targetWidth = image.width
      let targetHeight = image.height

      if (targetWidth > maxSize || targetHeight > maxSize) {
        const scale = Math.min(maxSize / targetWidth, maxSize / targetHeight)
        targetWidth = Math.round(targetWidth * scale)
        targetHeight = Math.round(targetHeight * scale)
      }

      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      canvas.width = targetWidth
      canvas.height = targetHeight
      context.drawImage(image, 0, 0, targetWidth, targetHeight)

      return new Promise((resolve, reject) => {
        canvas.toBlob(blob => {
          if (blob) resolve(blob)
          else reject(new Error('Unable to compress image'))
        }, file.type || 'image/png')
      })
    },
    async uploadImage(item) {
      item.status = 'uploading'
      item.message = this.$t('upload.uploading')

      const formData = new FormData()
      try {
        const blob = await this.compressImage(item.file)
        const compressedFile = new File([blob], item.file.name, {
          type: blob.type || item.file.type,
        })
        formData.append('image', compressedFile)
      } catch (error) {
        formData.append('image', item.file)
      }

      try {
        const response = await $upload({ url: '/users/upload', data: formData })
        if (response.ret !== 1) throw new Error(response.msg || 'Upload failed')

        item.status = 'success'
        item.message = ''
        this.form.image = response.data.path
      } catch (error) {
        item.status = 'failed'
        item.message = this.$t('upload.failed')
        this.form.image = ''
        Toast(this.$t('upload.failed'))
      }
    },
    deleteImage() {
      this.form.image = ''
    },
    async submitProduct() {
      const { name, price, image, description } = this.form
      if (!name || !price || !image || !description) {
        Toast(this.$t('publishProduct.incomplete'))
        return
      }
      if (this.submitting) return

      this.submitting = true
      try {
        const result = await PRODUCT_ADD({
          title: name,
          price,
          picture: [image],
          content: description,
        })
        if (!result || Number(result.ret) !== 1) {
          throw new Error((result && result.msg) || this.$t('Hint.failed'))
        }

        Toast(result.msg || this.$t('publishProduct.success'))
        this.form = {
          name: '',
          price: '',
          image: '',
          description: '',
        }
        this.fileList = []
        if (this.$refs.myProducts) this.$refs.myProducts.reloadProducts()
      } catch (error) {
        const message =
          (error && error.response && error.response.data && error.response.data.msg) ||
          (error && error.msg) ||
          (error && error.message) ||
          this.$t('Hint.failed')
        Toast(message)
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style lang="less" scoped>
@accent: var(--color-primary);
@text: var(--color-text);

.publish-product-page {
  min-height: 100vh;
  color: @text;
  background: var(--page-background);
}

.publish-product-page__content {
  padding: 10px 12px 24px;
}

.publish-card,
.products-card {
  background: #ffffff;
  border-radius: 11px;
  box-shadow: var(--card-shadow);
}

.publish-card {
  padding: 16px 14px;
}

.publish-card__title,
.products-card__title {
  margin-bottom: 10px;
  color: var(--color-primary-strong);
  font-size: 16px;
  text-align: center;
}

.publish-card__title {
  margin-bottom: 16px;
}

.form-field {
  display: block;
  margin-top: 14px;
}

.form-field__label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
}

.publish-input {
  align-items: center;
  padding: 11px 12px;
  background: #fafafa;
  border: 1px solid #dddddd;
  border-radius: 8px;
  transition: border-color 160ms ease, box-shadow 160ms ease;
  line-height: 1;
}

.publish-input--textarea {
  padding: 11px 12px;
}

.publish-input /deep/ .van-field__control {
  color: #333333;
  font-size: 15px;
}

.publish-input /deep/ .van-field__control::placeholder {
  color: #858585;
  opacity: 1;
}

.publish-input:focus-within {
  border-color: @accent;
  box-shadow: var(--focus-shadow);
}

.product-image-field {
  margin-top: 14px;
}

.uploader-row {
  min-height: 121px;
  display: flex;
  align-items: center;
  gap: 14px;
}

.upload-button {
  width: 96px;
  height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  color: var(--color-primary-strong);
  background: var(--color-primary-soft);
  border: 1px dashed var(--color-primary);
  border-radius: 11px;
}

.upload-button .van-icon {
  font-size: 25px;
}

.upload-button>div {
  font-size: 14px;
  line-height: 1;
}

.uploader-row /deep/ .van-uploader__preview,
.uploader-row /deep/ .van-uploader__preview-image {
  width: 120px;
  height: 120px;
  margin: 0;
  border-radius: 11px;
}

.uploader-row /deep/ .van-uploader__preview-image {
  overflow: hidden;
  border: 1px solid #eeeeee;
}

.file-name {
  min-width: 0;
  margin: 0;
  overflow: hidden;
  color: #999999;
  font-size: 14px;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.submit-button {
  width: calc(100% - 54px);
  display: block;
  margin: 22px auto 0;
  color: var(--button-text);
  background: var(--button-background);
  border: 0;
  border-radius: 9px;
}

.submit-button /deep/ .van-button__text {
  font-size: 18px;
  font-weight: 700;
}

.submit-button:active {
  opacity: 0.88;
}

.submit-button:disabled {
  cursor: not-allowed;
  opacity: 0.58;
}

.products-card {
  min-height: 138px;
  margin-top: 20px;
  padding: 20px 18px;
}

.products-card__empty {
  margin: 22px 0 0;
  color: #a0a0a0;
  font-size: 14px;
  line-height: 1.4;
  text-align: center;
}

@media (max-width: 360px) {
  .publish-product-page__content {
    padding-right: 12px;
    padding-left: 12px;
  }

  .publish-card {
    padding-right: 16px;
    padding-left: 16px;
  }

  .upload-button,
  .uploader-row /deep/ .van-uploader__preview,
  .uploader-row /deep/ .van-uploader__preview-image {
    width: 112px;
    height: 112px;
  }

  .submit-button {
    width: calc(100% - 30px);
  }
}
</style>
